import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    CircularProgress,
    Dialog,
    DialogContent,
    Box,
    Container,
    Divider,
    CardActions,
    Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoginRegisterComponent from './LoginRegister';
import { useAuth } from './AuthContext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import NoteIcon from '@mui/icons-material/Note';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { userProgressService } from './userProgressService';
import CopyrightDialog from './CopyrightDialog';
import PSINotificationModal from './PSINotificationModal';

const ActiveSubscriptions = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const { user, logout, checkTokenExpiration } = useAuth();
    const navigate = useNavigate();
    const [showCopyrightDialog, setShowCopyrightDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [showPSIModal, setShowPSIModal] = useState(false);
    const [hasShownPSIModal, setHasShownPSIModal] = useState(false);

    useEffect(() => {
        if (user) {
            fetchSubscriptions();
            
            // Check if we should show PSI modal for users who just logged in
            const justLoggedIn = sessionStorage.getItem('justLoggedIn');
            
            if (justLoggedIn === 'true') {
                sessionStorage.removeItem('justLoggedIn');
                const hidePSINotification = localStorage.getItem('hidePSINotification');
                
                if (hidePSINotification !== 'true') {
                    // Small delay to ensure smooth transition
                    setTimeout(() => {
                        setShowPSIModal(true);
                        setHasShownPSIModal(true);
                    }, 1000);
                }
            }
        } else {
            setLoading(false);
            setShowLoginDialog(true);
        }
    }, [user]);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            if (!checkTokenExpiration()) {
                throw new Error('Token expired');
            }
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/subscriptions/getMySubscriptions`,
            );
            setSubscriptions(response.data.data.subscriptions);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
            if (
                err.response?.status === 401 ||
                err.message === 'Token expired'
            ) {
                logout();
                setShowLoginDialog(true);
            } else {
                setError('Failed to fetch subscriptions. Please try again.');
            }
            setLoading(false);
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginDialog(false);
        fetchSubscriptions();
        
        // Show PSI notification modal after successful login
        const hidePSINotification = localStorage.getItem('hidePSINotification');
        
        if (hidePSINotification !== 'true') {
            // Small delay to ensure dialog closes smoothly
            setTimeout(() => {
                setShowPSIModal(true);
                setHasShownPSIModal(true);
            }, 1000);
        }
    };

    const handleStartTest = async (subscriptionId, testId, action) => {
        setPendingAction(() => async () => {
            const correctSubscription = subscriptions.filter(
                (sub) => sub.id === subscriptionId,
            );
            const subscription = correctSubscription[0];
            try {
                console.log('Full subscription:', subscription);
                console.log('Subscription product:', subscription.product[0]);
                console.log(
                    'allowedTest:',
                    subscription.product[0].allowedTest,
                );

                // Use the testId parameter that was passed to the function
                const extractedTestId = testId;

                const progress = await userProgressService
                    .getUserProgress(subscriptionId, extractedTestId)
                    .catch((error) => {
                        console.error(
                            'Progress fetch error:',
                            error.response?.data,
                        );
                        if (error.response?.status === 404) {
                            return null;
                        }
                        throw error;
                    });

                console.log('Retrieved progress:', progress);

                if (action === 'resume' && !progress) {
                    setError(
                        'No progress found to resume. Please start a new test.',
                    );
                    return;
                }

                localStorage.setItem(
                    `testAction_${subscriptionId}_${testId}`,
                    action,
                );

                if (action === 'start') {
                    localStorage.removeItem(
                        `testProgress_${subscriptionId}_${testId}`,
                    );
                }

                navigate(`/viewTests/${subscriptionId}?test=${encodeURIComponent(testId)}`);
            } catch (error) {
                console.error('Test start error:', error);
                if (error.message === 'Token expired') {
                    logout();
                    setShowLoginDialog(true);
                } else {
                    setError(
                        error.response?.data?.message ||
                            `Failed to ${action} the test. Please try again.`,
                    );
                }
            }
        });
        setShowCopyrightDialog(true);
    };

    const handleViewNoteCards = (subscriptionId, testId) => {
        setPendingAction(() => () => {
            if (checkTokenExpiration()) {
                navigate(`/noteCards/${subscriptionId}?test=${encodeURIComponent(testId)}`);
            } else {
                logout();
                setShowLoginDialog(true);
            }
        });
        setShowCopyrightDialog(true);
    };

    const handleCopyrightAgree = async () => {
        setShowCopyrightDialog(false);
        if (pendingAction) {
            await pendingAction();
            setPendingAction(null);
        }
    };

    const handleCopyrightDecline = () => {
        setShowCopyrightDialog(false);
        setPendingAction(null);
    };

    const isSubscriptionActive = (endDate) => {
        return new Date(endDate) > new Date();
    };

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                sx={{ gap: 2 }}
            >
                <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                        color: '#ff5e1c',
                        '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                        }
                    }}
                />
                <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
                    Loading your subscriptions...
                </Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
            {/* Header Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c5a 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 4,
                    boxShadow: '0 8px 32px rgba(255, 94, 28, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '40%',
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        transform: 'translate(30%, -30%)',
                    }
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ position: 'relative', zIndex: 1 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                                mb: 1,
                                textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                            }}
                        >
                            My Study Programs
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '1.1rem',
                                fontWeight: 400
                            }}
                        >
                            Access your active subscriptions and continue learning
                        </Typography>
                    </Box>
                    {user && (
                        <Button
                            variant="contained"
                            startIcon={<AssessmentIcon />}
                            onClick={() => navigate('/test-history')}
                            sx={{
                                backgroundColor: '#fff',
                                color: '#ff5e1c',
                                fontWeight: 600,
                                px: 3,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            View My Stats
                        </Button>
                    )}
                </Box>
            </Box>
            <Grid container spacing={3}>
                {subscriptions.length === 0 ? (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                px: 3,
                                backgroundColor: '#f8f8f8',
                                borderRadius: 3,
                                border: '2px dashed #ddd'
                            }}
                        >
                            <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
                                No Active Subscriptions
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#999', mb: 3 }}>
                                Start your learning journey by purchasing a study program
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/shop')}
                                sx={{
                                    backgroundColor: '#ff5e1c',
                                    color: '#fff',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                                    '&:hover': {
                                        backgroundColor: '#e54e0c',
                                        boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Browse Study Programs
                            </Button>
                        </Box>
                    </Grid>
                ) : (
                    subscriptions.map((subscription) => (
                        <Grid item xs={12} sm={6} md={4} key={subscription._id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #f0f0f0',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 40px rgba(255, 94, 28, 0.15)',
                                        borderColor: '#ff5e1c',
                                    },
                                }}
                            >
                                {/* Card Header with Gradient */}
                                <Box
                                    sx={{
                                        background: 'linear-gradient(135deg, #0a1229 0%, #1a2847 100%)',
                                        p: 3,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -50,
                                            right: -50,
                                            width: 150,
                                            height: 150,
                                            background: 'rgba(255, 94, 28, 0.15)',
                                            borderRadius: '50%',
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '1.2rem',
                                            mb: 1,
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    >
                                        {subscription.product[0]?.name || 'Unknown Product'}
                                    </Typography>
                                    <Chip
                                        label={
                                            isSubscriptionActive(subscription.endDate)
                                                ? 'Active'
                                                : 'Expired'
                                        }
                                        sx={{
                                            backgroundColor: isSubscriptionActive(subscription.endDate)
                                                ? '#4caf50'
                                                : '#f44336',
                                            color: '#fff',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            height: 24,
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    />
                                </Box>

                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#999',
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                                fontWeight: 600,
                                                fontSize: '0.7rem'
                                            }}
                                        >
                                            Subscription Period
                                        </Typography>
                                        <Divider sx={{ my: 1, borderColor: '#f0f0f0' }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.7rem' }}>
                                                    Start Date
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                                                    {new Date(subscription.startDate).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.7rem' }}>
                                                    End Date
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                                                    {new Date(subscription.endDate).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                                {subscription.product[0] &&
                                    isSubscriptionActive(
                                        subscription.endDate,
                                    ) && (
                                        <CardActions
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                p: 3,
                                                pt: 0,
                                                gap: 1.5,
                                                backgroundColor: '#fafafa',
                                            }}
                                        >
                                            {/* Render buttons for multiple tests if available */}
                                            {subscription.product[0].allowedTests && subscription.product[0].allowedTests.length > 0 ? (
                                                subscription.product[0].allowedTests.map((test, index) => (
                                                    <Box key={test._id || test} sx={{ mb: index < subscription.product[0].allowedTests.length - 1 ? 2 : 0 }}>
                                                        {subscription.product[0].allowedTests.length > 1 && (
                                                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 700, color: '#0a1229', fontSize: '0.9rem' }}>
                                                                Test {index + 1}: {test.name || 'Unknown Test'}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<PlayArrowIcon />}
                                                                onClick={() =>
                                                                    handleStartTest(
                                                                        subscription._id,
                                                                        test._id || test,
                                                                        'resume',
                                                                    )
                                                                }
                                                                fullWidth
                                                                sx={{
                                                                    backgroundColor: '#ff5e1c',
                                                                    color: '#fff',
                                                                    fontWeight: 600,
                                                                    py: 1.25,
                                                                    borderRadius: 2,
                                                                    boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                                                                    textTransform: 'none',
                                                                    fontSize: '0.95rem',
                                                                    '&:hover': {
                                                                        backgroundColor: '#e54e0c',
                                                                        boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                                                        transform: 'translateY(-2px)',
                                                                    },
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                            >
                                                                Start Test
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                startIcon={<NoteIcon />}
                                                                onClick={() =>
                                                                    handleViewNoteCards(
                                                                        subscription._id,
                                                                        test._id || test,
                                                                    )
                                                                }
                                                                fullWidth
                                                                sx={{
                                                                    borderColor: '#0a1229',
                                                                    color: '#0a1229',
                                                                    fontWeight: 600,
                                                                    py: 1.25,
                                                                    borderRadius: 2,
                                                                    borderWidth: 2,
                                                                    textTransform: 'none',
                                                                    fontSize: '0.95rem',
                                                                    '&:hover': {
                                                                        borderColor: '#0a1229',
                                                                        backgroundColor: 'rgba(10, 18, 41, 0.04)',
                                                                        borderWidth: 2,
                                                                        transform: 'translateY(-2px)',
                                                                    },
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                            >
                                                                Note Cards
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ))
                                            ) : (
                                                /* Fallback to single test for backward compatibility */
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<PlayArrowIcon />}
                                                        onClick={() =>
                                                            handleStartTest(
                                                                subscription._id,
                                                                subscription.product[0]
                                                                    .allowedTest,
                                                                'resume',
                                                            )
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            backgroundColor: '#ff5e1c',
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                            py: 1.25,
                                                            borderRadius: 2,
                                                            boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                                                            textTransform: 'none',
                                                            fontSize: '0.95rem',
                                                            '&:hover': {
                                                                backgroundColor: '#e54e0c',
                                                                boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        Start Test
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<NoteIcon />}
                                                        onClick={() =>
                                                            handleViewNoteCards(
                                                                subscription._id,
                                                                subscription.product[0]
                                                                    .allowedTest,
                                                            )
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            borderColor: '#0a1229',
                                                            color: '#0a1229',
                                                            fontWeight: 600,
                                                            py: 1.25,
                                                            borderRadius: 2,
                                                            borderWidth: 2,
                                                            textTransform: 'none',
                                                            fontSize: '0.95rem',
                                                            '&:hover': {
                                                                borderColor: '#0a1229',
                                                                backgroundColor: 'rgba(10, 18, 41, 0.04)',
                                                                borderWidth: 2,
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        Note Cards
                                                    </Button>
                                                </>
                                            )}
                                        </CardActions>
                                    )}
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            <CopyrightDialog
                open={showCopyrightDialog}
                onClose={handleCopyrightDecline}
                onAgree={handleCopyrightAgree}
            />
            
            {/* PSI Notification Modal */}
            <PSINotificationModal 
                open={showPSIModal} 
                onClose={() => setShowPSIModal(false)} 
            />

            <Dialog
                open={showLoginDialog}
                onClose={() => setShowLoginDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                        background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(10, 18, 41, 0.75)',
                        backdropFilter: 'blur(4px)',
                    }
                }}
            >
                <DialogContent sx={{ p: 0, '&:first-of-type': { paddingTop: 0 } }}>
                    <LoginRegisterComponent
                        onLoginSuccess={handleLoginSuccess}
                    />
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default ActiveSubscriptions;
